<template>
  <div class="container">
    <h1>{{ $t('title') }}</h1>
    <h2>{{ $t('subtitle') }}</h2>
    <gs1-alert-loading
      v-if="loading"
      style="margin-top: 30px"
    ></gs1-alert-loading>
    <gs1-alert-search-term-errors
      v-if="!loading && searchTermErrors.length > 0"
      style="margin-top: 30px"
      :ids="searchTermErrors"
    ></gs1-alert-search-term-errors>

    <div class="row" style="margin-top: 60px; margin-bottom: 10px">
      <div class="col">
        <div style="display: flex">
          <form style="margin-left: 15px" class="form-inline" @submit.prevent>
            <div class="form-group">
              <div class="checkbox">
                <label>
                  <input type="checkbox" v-model="completeOnly" />
                  {{ $t('only_complete') }}
                </label>
              </div>
            </div>
            <div class="form-group form-search-box">
              <div class="input-wrapper icon-search">
                <input
                  type="text"
                  name="search"
                  class="form-control"
                  :placeholder="$t('search')"
                  v-model="search"
                />
              </div>
            </div>
          </form>

          <div
            style="
              margin-left: auto;
              margin-right: 15px;
              position: relative;
              bottom: 10px;
            "
          >
            <button
              style="margin-right: 10px"
              :disabled="!canExport"
              type="button"
              class="btn btn-secondary btn-sm"
              @click="exportResults('csv')"
            >
              CSV
            </button>
            <button
              :disabled="!canExport"
              type="button"
              class="btn btn-secondary btn-sm"
              @click="exportResults('xlsx')"
            >
              XLSX
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="table-responsive">
      <table class="table table-bordered table-striped">
        <colgroup>
          <col style="width: 65px" />
          <col />
          <col />
        </colgroup>
        <thead>
          <tr>
            <th style="border-right: none"></th>
            <th style="border-left: none; font-weight: bold">
              {{ $t('product_info') }}
            </th>
            <th style="font-weight: bold">{{ $t('gtin') }}</th>
          </tr>
        </thead>

        <tbody
          v-if="
            typeof filteredGTINResults !== 'undefined' &&
            filteredGTINResults.length > 0
          "
        >
          <tr
            v-for="(result, i) in filteredGTINResults"
            :key="i"
            :class="{
              error: result.error && result.error.code === 'NOT_FOUND'
            }"
          >
            <td class="image" v-if="result.error">
              <img class="img-responsive" src="/static/images/unknown.png" />
            </td>
            <td class="image" v-else>
              <router-link
                :to="{
                  name: 'Result',
                  params: { search: result.gtin },
                  query: { bulk: null }
                }"
              >
                <img
                  class="img-responsive"
                  v-if="
                    typeof result.productImageUrl !== 'undefinded' &&
                    Array.isArray(result.productImageUrl) &&
                    result.productImageUrl.length > 0
                  "
                  :src="result.productImageUrl[0].value"
                />
                <img
                  class="img-responsive"
                  v-else
                  src="/static/images/unknown.png"
                />
              </router-link>
            </td>
            <td class="description" v-if="result.error">
              <i18n
                v-if="result.error.code === 'NOT_FOUND'"
                path="errors.NOT_FOUND"
                tag="strong"
              >
                <a
                  href="https://www.gs1-germany.de/service/kontakt/"
                  target="_blank"
                  rel="noopener"
                >
                  {{ $t('contact_gs1') }}
                </a>
              </i18n>
              <i18n
                v-else-if="result.error.code === 'REGISTERED'"
                path="errors.REGISTERED"
                tag="strong"
              >
                <b style="color: var(--gs1-blue)">{{ result.licenseeName }}</b>
              </i18n>
              <i18n v-else :path="`errors.${result.error.code}`" tag="strong">
                <router-link
                  :to="{
                    name: 'Result',
                    params: { search: result.error.suggestion, bulk: 'yes' },
                    query: { bulk: null }
                  }"
                >
                  {{ result.error.suggestion }}
                </router-link>
              </i18n>
            </td>
            <td class="description" v-else>
              <div v-if="result.productDescription">
                <router-link
                  v-for="description in result.productDescription"
                  :key="description.language"
                  :to="{
                    name: 'Result',
                    params: { search: result.gtin },
                    query: { bulk: null }
                  }"
                >
                  <lang-string
                    :lang="description.language"
                    :value="description.value"
                  />
                </router-link>
              </div>
              <div v-else-if="result.productDescription">
                <i18n path="registered_to">
                  {{ result.licenseeName }}
                </i18n>
              </div>
              <div v-else>
                {{ $t('nothing_found') }}
              </div>
            </td>
            <td class="gtin">
              <router-link
                :to="{
                  name: 'Result',
                  params: { search: result.gtin },
                  query: { bulk: null }
                }"
              >
                {{ result.gtin }}
              </router-link>
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr>
            <td colspan="3" class="text-center text-muted">
              {{ $t('no_data') }}
            </td>
          </tr>
        </tbody>

        <thead
          style="
            border-right: 1px solid #fbfbfb;
            border-left: 1px solid #fbfbfb;
          "
        >
          <tr>
            <th style="border-left: none; border-right: none"></th>
            <th style="border-left: none; border-right: none"></th>
            <th style="border-left: none; border-right: none"></th>
          </tr>
        </thead>

        <thead>
          <tr>
            <th style="border-right: none"></th>
            <th style="border-left: none; font-weight: bold">
              {{ $t('location_info') }}
            </th>
            <th style="font-weight: bold">{{ $t('gln') }}</th>
          </tr>
        </thead>
        <tbody
          v-if="
            typeof filteredGLNResults !== 'undefined' &&
            filteredGLNResults.length > 0
          "
        >
          <tr
            v-for="(result, i) in filteredGLNResults"
            :key="i"
            :class="{
              error: result.error && result.error.code === 'NOT_FOUND'
            }"
          >
            <td style="border-right: none"></td>
            <td class="description" v-if="result.error">
              <i18n
                v-if="result.error.code === 'NOT_FOUND'"
                path="errors.NOT_FOUND"
                tag="strong"
              >
                <a
                  href="https://www.gs1-germany.de/service/kontakt/"
                  target="_blank"
                  rel="noopener"
                >
                  {{ $t('contact_gs1') }}
                </a>
              </i18n>
              <i18n
                v-else-if="result.error.code === 'REGISTERED'"
                path="errors.REGISTERED"
                tag="strong"
              >
                <b style="color: var(--gs1-blue)">{{
                  result.gs1Licence.licenseeName
                }}</b>
              </i18n>
              <i18n v-else :path="`errors.${result.error.code}`" tag="strong">
                <router-link
                  :to="{
                    name: 'Result',
                    params: { search: result.error.suggestion, bulk: 'yes' },
                    query: { bulk: null }
                  }"
                >
                  {{ result.error.suggestion }}
                </router-link>
              </i18n>
            </td>
            <td class="description" v-else style="border-left: none">
              <div v-if="result.gs1Licence.licenseeName">
                <i18n path="registered_to_gln">
                  <router-link
                    :to="{
                      name: 'Result',
                      params: { search: result.gln },
                      query: { bulk: null }
                    }"
                  >
                    {{ result.gs1Licence.licenseeName }}
                  </router-link>
                </i18n>
              </div>
              <div v-else-if="result.gs1Licence.licenseeName">
                <i18n path="registered_to">
                  {{ result.gs1Licence.licenseeName }}
                </i18n>
              </div>
              <div v-else>
                {{ $t('nothing_found') }}
              </div>
            </td>
            <td class="gtin">
              <router-link
                :to="{
                  name: 'Result',
                  params: { search: result.gln },
                  query: { bulk: null }
                }"
              >
                {{ result.gln }}
              </router-link>
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr>
            <td colspan="3" class="text-center text-muted">
              {{ $t('no_data') }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { mapProduct } from '@/mixins/mapProduct'
import { saveAs } from 'file-saver'
import { validateGtin } from '@/mixins/validateGtin'
import Gs1AlertLoading from '@/components/alerts/Gs1AlertLoading'
import Gs1AlertSearchTermErrors from '@/components/alerts/Gs1AlertSearchTermErrors'
import LangString from '@/components/LangString'
import ResultData from '@/components/ResultData'
import { saveGet } from '../../mixins/saveGet'
import * as XLSX from 'xlsx'
import * as jp from 'jsonpath'

export default {
  props: ['gtins'],
  components: {
    Gs1AlertLoading,
    Gs1AlertSearchTermErrors,
    LangString,
    ResultData
  },
  mixins: [mapProduct, validateGtin, saveGet],
  data() {
    return {
      completeOnly: false,
      searchTermErrors: [],
      loading: false,
      GTINresults: [],
      GLNresults: [],
      search: ''
    }
  },
  computed: {
    canExport() {
      return this.GTINresults.length > 0 || this.GLNresults.length > 0
    },
    filteredGTINResults() {
      let results = this.GTINresults

      if (this.completeOnly) {
        results = results.filter(result => {
          if (result.error) return false
          return result.isComplete
        })
      }

      if (this.search) {
        results = results.filter(result => {
          const lowerCaseSearch = this.search.toLowerCase()
          if (result.gtin.includes(lowerCaseSearch)) return true
          if (result.error) return false
          if (!result.productDescription) return false
          return result.productDescription.some(r =>
            r.value.toLowerCase().includes(lowerCaseSearch)
          )
        })
      }

      return results.sort((a, b) => a.gtin - b.gtin)
    },
    filteredGLNResults() {
      let results = this.GLNresults
      if (this.search) {
        results = results.filter(result => {
          const lowerCaseSearch = this.search.toLowerCase()
          if (result.gln.includes(lowerCaseSearch)) return true
          if (
            result.gs1Licence &&
            result.gs1Licence.licenseeName &&
            result.gs1Licence.licenseeName
              .toLowerCase()
              .includes(lowerCaseSearch)
          )
            return true
          if (result.error) return false
        })
      }
      return results.sort((a, b) => a.gln - b.gln)
    }
  },
  created() {
    this.load()
  },
  watch: {
    $route: 'load'
  },
  methods: {
    toCSVString(resultObjects) {
      const headers = Object.keys(resultObjects[0]).join(',')
      const csv = [headers].concat(
        resultObjects.map(res => {
          return Object.values(res).join(',')
        })
      )
      return csv.join('\n')
    },
    getLangProp(value) {
      if (!value) return value
      const langArray = Array.isArray(value) ? value : [value]
      if (!langArray[0].value || !langArray[0].language) return value
      const valProp = (
        langArray.find(e => e.language == this.$i18n.locale) || langArray[0]
      ).value
      const langProp = langArray.find(e => e.language == this.$i18n.locale)
        ? this.$i18n.locale
        : langArray[0].language
      return valProp + (' (' + langProp + ')')
    },
    resultToFlatObject(JSONPathDict, results) {
      return results.map(res => {
        let returnObject = {}
        Object.entries(JSONPathDict).forEach(([k, v]) => {
          const value = jp.query(res, '$.' + v)[0]
          let assignValue = value
          let keyDecription = ''
          const isLangValue =
            Array.isArray(value) && value.find(e => e.language)
          const isUnitCode = Array.isArray(value) && value.find(e => e.unitCode)
          const isCountryCode =
            Array.isArray(value) &&
            value.find(e => e.numeric || e.alpha2 || e.alpha3)
          const isAddress =
            (Array.isArray(value) &&
              value.find(e => e.countryCode && e.postalCode)) ||
            (value && value.countryCode && value.postalCode)
          const isGeo = value && value.latitude && value.longitude
          if (isLangValue) {
            assignValue = this.getLangProp(value)
          } else if (isGeo) {
            assignValue = value.latitude + '/' + value.longitude
            keyDecription = ' (lat/long)'
          } else if (isUnitCode) {
            assignValue = value[0].value
            assignValue += ' ' + this.$t('uom.' + value[0].unitCode)
          } else if (isCountryCode) {
            assignValue = value
              .map(
                cc =>
                  this.$t('countries.' + cc.alpha2) ||
                  (cc.numeric || '-') +
                    '/' +
                    (cc.alpha2 || '-') +
                    '/' +
                    (cc.alpha3 || '-')
              )
              .join(';')
          } else if (isAddress) {
            const addressArray = Array.isArray(value) ? value : [value]
            assignValue = addressArray
              .map(address => {
                return (
                  (this.getLangProp(
                    this.saveGet(address, null, 'countryCode')
                  ) || '-') +
                  '/' +
                  (this.getLangProp(
                    this.saveGet(address, null, 'postalCode')
                  ) || '-') +
                  '/' +
                  (this.getLangProp(
                    this.saveGet(address, null, 'addressLocality', 'value')
                  ) || '-') +
                  '/' +
                  (this.getLangProp(
                    this.saveGet(address, null, 'streetAddress', 'value')
                  ) || '-') +
                  '/' +
                  (this.getLangProp(
                    this.saveGet(address, null, 'postalName', 'value')
                  ) || '-') +
                  '/'
                )
              })
              .join(';')
          } else if (Array.isArray(value)) {
            assignValue = value
              .map(e => {
                const key = v.split('.').at(-1)
                if (key == 'organizationRole')
                  return this.$t('locationType.' + e + '_title')
                if (key == 'locationRole')
                  return this.$t('locationType.' + e + '_title')
                if (typeof e === 'object') return Object.values(e).join('/')
                return e
              })
              .join(';')
          }
          returnObject[k + keyDecription] = assignValue
        })
        return returnObject
      })
    },
    exportResults(format) {
      const gtinEntries = {
        [this.$t('gtin')]: 'gtin',
        [this.$t('brandName')]: 'brandName',
        [this.$t('productDescription')]: 'productDescription',
        [this.$t('productImageUrl')]: 'productImageUrl',
        [this.$t('gpcCategoryCode')]: 'gpcCategoryCode',
        [this.$t('netContent')]: 'netContent',
        [this.$t('countryOfSaleCode')]: 'countryOfSaleCode',
        [this.$t('address')]: 'gs1Licence.address',
        [this.$t('contact')]: 'gs1Licence.contactPoint',
        [this.$t('licenceKey')]: 'gs1Licence.licenceKey',
        [this.$t('licenceType')]: 'gs1Licence.licenceType',
        [this.$t('licenseeName')]: 'gs1Licence.licenseeName',
        [this.$t('licenseeGLN')]: 'gs1Licence.licenseeGLN',
        [this.$t('licensingMO')]: 'gs1Licence.licensingMO.moName',
        [this.$t('updatedOn')]: 'gs1Licence.dateUpdated'
      }

      const glnEntries = {
        [this.$t('gln')]: 'gln',
        [this.$t('glnRecordStatus')]: 'glnRecordStatus',
        ['(' + this.$t('digital_location') + ') ' + this.$t('glnRecordStatus')]:
          'glnRecords[?(@.glnType=="DIGITAL_LOCATION")].glnRecordStatus',
        ['(' + this.$t('digital_location') + ') ' + this.$t('DL_name')]:
          'glnRecords[?(@.glnType=="DIGITAL_LOCATION")].digitalLocationName',
        ['(' +
        this.$t('digital_location') +
        ') ' +
        this.$t('DL_digital_address')]:
          'glnRecords[?(@.glnType=="DIGITAL_LOCATION")].digitalAddress',
        ['(' + this.$t('digital_location') + ') ' + this.$t('DL_role')]:
          'glnRecords[?(@.glnType=="DIGITAL_LOCATION")].locationRole',
        ['(' +
        this.$t('digital_location') +
        ') ' +
        this.$t('DL_more_locations')]:
          'glnRecords[?(@.glnType=="DIGITAL_LOCATION")].additionalLocationID',
        ['(' + this.$t('digital_location') + ') ' + this.$t('contact')]:
          'glnRecords[?(@.glnType=="DIGITAL_LOCATION")].contactPoint',
        ['(' + this.$t('digital_location') + ') ' + this.$t('updatedOn')]:
          'glnRecords[?(@.glnType=="DIGITAL_LOCATION")].dateUpdated',
        ['(' + this.$t('function') + ') ' + this.$t('glnRecordStatus')]:
          'glnRecords[?(@.glnType=="FUNCTION")].glnRecordStatus',
        ['(' + this.$t('function') + ') ' + this.$t('FN_name')]:
          'glnRecords[?(@.glnType=="FUNCTION")].department',
        ['(' + this.$t('function') + ') ' + this.$t('address')]:
          'glnRecords[?(@.glnType=="FUNCTION")].address',
        ['(' + this.$t('function') + ') ' + this.$t('FN_role')]:
          'glnRecords[?(@.glnType=="FUNCTION")].organizationRole',
        ['(' + this.$t('function') + ') ' + this.$t('parent_company')]:
          'glnRecords[?(@.glnType=="FUNCTION")].parentOrganization.gln',
        ['(' + this.$t('function') + ') ' + this.$t('FN_more_organizations')]:
          'glnRecords[?(@.glnType=="FUNCTION")].additionalOrganizationID',
        ['(' + this.$t('function') + ') ' + this.$t('updatedOn')]:
          'glnRecords[?(@.glnType=="FUNCTION")].dateUpdated',
        ['(' + this.$t('legal_entity') + ') ' + this.$t('glnRecordStatus')]:
          'glnRecords[?(@.glnType=="LEGAL_ENTITY")].glnRecordStatus',
        ['(' + this.$t('legal_entity') + ') ' + this.$t('LE_name')]:
          'glnRecords[?(@.glnType=="LEGAL_ENTITY")].organizationName',
        ['(' + this.$t('legal_entity') + ') ' + this.$t('parent_company')]:
          'glnRecords[?(@.glnType=="LEGAL_ENTITY")].parentOrganization.gln',
        ['(' + this.$t('legal_entity') + ') ' + this.$t('LE_role')]:
          'glnRecords[?(@.glnType=="LEGAL_ENTITY")].organizationRole',
        ['(' + this.$t('legal_entity') + ') ' + this.$t('address')]:
          'glnRecords[?(@.glnType=="LEGAL_ENTITY")].address',
        ['(' + this.$t('legal_entity') + ') ' + this.$t('contact')]:
          'glnRecords[?(@.glnType=="LEGAL_ENTITY")].contactPoint',
        ['(' + this.$t('legal_entity') + ') ' + this.$t('updatedOn')]:
          'glnRecords[?(@.glnType=="LEGAL_ENTITY")].dateUpdated',
        ['(' +
        this.$t('fixed_physical_location') +
        ') ' +
        this.$t('glnRecordStatus')]:
          'glnRecords[?(@.glnType=="FIXED_PHYSICAL_LOCATION")].glnRecordStatus',
        ['(' + this.$t('fixed_physical_location') + ') ' + this.$t('PL_name')]:
          'glnRecords[?(@.glnType=="FIXED_PHYSICAL_LOCATION")].physicalLocationName',
        ['(' + this.$t('fixed_physical_location') + ') ' + this.$t('PL_role')]:
          'glnRecords[?(@.glnType=="FIXED_PHYSICAL_LOCATION")].locationRole',
        ['(' +
        this.$t('fixed_physical_location') +
        ') ' +
        this.$t('PL_more_locations')]:
          'glnRecords[?(@.glnType=="FIXED_PHYSICAL_LOCATION")].additionalLocationID',
        ['(' + this.$t('fixed_physical_location') + ') ' + this.$t('address')]:
          'glnRecords[?(@.glnType=="FIXED_PHYSICAL_LOCATION")].address',
        ['(' + this.$t('fixed_physical_location') + ') ' + this.$t('contact')]:
          'glnRecords[?(@.glnType=="FIXED_PHYSICAL_LOCATION")].contactPoint',
        ['(' + this.$t('fixed_physical_location') + ') ' + this.$t('geo')]:
          'glnRecords[?(@.glnType=="FIXED_PHYSICAL_LOCATION")].geoCoordinates',
        ['(' +
        this.$t('fixed_physical_location') +
        ') ' +
        this.$t('updatedOn')]:
          'glnRecords[?(@.glnType=="FIXED_PHYSICAL_LOCATION")].dateUpdated',
        ['(' + this.$t('mobile_physical_location') + ') ' + this.$t('PL_name')]:
          'glnRecords[?(@.glnType=="MOBILE_PHYSICAL_LOCATION")].physicalLocationName',
        ['(' + this.$t('mobile_physical_location') + ') ' + this.$t('ML_role')]:
          'glnRecords[?(@.glnType=="MOBILE_PHYSICAL_LOCATION")].locationRole',
        ['(' +
        this.$t('mobile_physical_location') +
        ') ' +
        this.$t('PL_base_location')]:
          'glnRecords[?(@.glnType=="MOBILE_PHYSICAL_LOCATION")].baseLocation.gln',
        ['(' + this.$t('mobile_physical_location') + ') ' + this.$t('contact')]:
          'glnRecords[?(@.glnType=="MOBILE_PHYSICAL_LOCATION")].contactPoint',
        ['(' +
        this.$t('mobile_physical_location') +
        ') ' +
        this.$t('updatedOn')]:
          'glnRecords[?(@.glnType=="MOBILE_PHYSICAL_LOCATION")].dateUpdated',
        [this.$t('address')]: 'gs1Licence.address',
        [this.$t('contact')]: 'gs1Licence.contactPoint',
        [this.$t('licenceKey')]: 'gs1Licence.licenceKey',
        [this.$t('licenceType')]: 'gs1Licence.licenceType',
        [this.$t('licenseeName')]: 'gs1Licence.licenseeName',
        [this.$t('licenseeGLN')]: 'gs1Licence.licenseeGLN',
        [this.$t('licensingMO')]: 'gs1Licence.licensingMO.moName',
        [this.$t('updatedOn')]: 'gs1Licence.dateUpdated'
      }

      const gtinLicence = {
        [this.$t('gtin')]: 'gtin',
        [this.$t('licenseeName')]: 'gs1Licence.licenseeName',
        [this.$t('address')]: 'gs1Licence.address',
        [this.$t('contact')]: 'gs1Licence.contactPoint',
        [this.$t('licenceType')]: 'gs1Licence.licenceType',
        [this.$t('licenseeGLN')]: 'gs1Licence.licenseeGLN',
        [this.$t('licensingMO')]: 'gs1Licence.licensingMO.moName',
        [this.$t('updatedOn')]: 'gs1Licence.dateUpdated'
      }

      const glnLicence = {
        [this.$t('gln')]: 'gln',
        [this.$t('address')]: 'gs1Licence.address',
        [this.$t('contact')]: 'gs1Licence.contactPoint',
        [this.$t('licenceKey')]: 'gs1Licence.licenceKey',
        [this.$t('licenceType')]: 'gs1Licence.licenceType',
        [this.$t('licenseeName')]: 'gs1Licence.licenseeName',
        [this.$t('licenseeGLN')]: 'gs1Licence.licenseeGLN',
        [this.$t('licensingMO')]: 'gs1Licence.licensingMO.moName',
        [this.$t('updatedOn')]: 'gs1Licence.dateUpdated'
      }

      const glnDLEntries = {
        [this.$t('gln')]: 'gln',
        [this.$t('glnRecordStatus')]:
          'glnRecords[?(@.glnType=="DIGITAL_LOCATION")].glnRecordStatus',
        [this.$t('DL_name')]:
          'glnRecords[?(@.glnType=="DIGITAL_LOCATION")].digitalLocationName',
        [this.$t('DL_digital_address')]:
          'glnRecords[?(@.glnType=="DIGITAL_LOCATION")].digitalAddress',
        [this.$t('DL_role')]:
          'glnRecords[?(@.glnType=="DIGITAL_LOCATION")].locationRole',
        [this.$t('DL_more_locations')]:
          'glnRecords[?(@.glnType=="DIGITAL_LOCATION")].additionalLocationID',
        [this.$t('contact')]:
          'glnRecords[?(@.glnType=="DIGITAL_LOCATION")].contactPoint',
        [this.$t('updatedOn')]:
          'glnRecords[?(@.glnType=="DIGITAL_LOCATION")].dateUpdated'
      }

      const glnFNEntries = {
        [this.$t('gln')]: 'gln',
        [this.$t('glnRecordStatus')]:
          'glnRecords[?(@.glnType=="FUNCTION")].glnRecordStatus',
        [this.$t('FN_name')]: 'glnRecords[?(@.glnType=="FUNCTION")].department',
        [this.$t('address')]: 'glnRecords[?(@.glnType=="FUNCTION")].address',
        [this.$t('FN_role')]:
          'glnRecords[?(@.glnType=="FUNCTION")].organizationRole',
        [this.$t('parent_company')]:
          'glnRecords[?(@.glnType=="FUNCTION")].parentOrganization.gln',
        [this.$t('FN_more_organizations')]:
          'glnRecords[?(@.glnType=="FUNCTION")].additionalOrganizationID',
        [this.$t('updatedOn')]:
          'glnRecords[?(@.glnType=="FUNCTION")].dateUpdated'
      }

      const glnLEEntries = {
        [this.$t('gln')]: 'gln',
        [this.$t('glnRecordStatus')]:
          'glnRecords[?(@.glnType=="LEGAL_ENTITY")].glnRecordStatus',
        [this.$t('LE_name')]:
          'glnRecords[?(@.glnType=="LEGAL_ENTITY")].organizationName',
        [this.$t('parent_company')]:
          'glnRecords[?(@.glnType=="LEGAL_ENTITY")].parentOrganization.gln',
        [this.$t('LE_role')]:
          'glnRecords[?(@.glnType=="LEGAL_ENTITY")].organizationRole',
        [this.$t('address')]:
          'glnRecords[?(@.glnType=="LEGAL_ENTITY")].address',
        [this.$t('contact')]:
          'glnRecords[?(@.glnType=="LEGAL_ENTITY")].contactPoint',
        [this.$t('updatedOn')]:
          'glnRecords[?(@.glnType=="LEGAL_ENTITY")].dateUpdated'
      }

      const glnPLEntries = {
        [this.$t('gln')]: 'gln',
        [this.$t('glnRecordStatus')]:
          'glnRecords[?(@.glnType=="FIXED_PHYSICAL_LOCATION")].glnRecordStatus',
        [this.$t('PL_name')]:
          'glnRecords[?(@.glnType=="FIXED_PHYSICAL_LOCATION")].physicalLocationName',
        [this.$t('PL_role')]:
          'glnRecords[?(@.glnType=="FIXED_PHYSICAL_LOCATION")].locationRole',
        [this.$t('PL_more_locations')]:
          'glnRecords[?(@.glnType=="FIXED_PHYSICAL_LOCATION")].additionalLocationID',
        [this.$t('address')]:
          'glnRecords[?(@.glnType=="FIXED_PHYSICAL_LOCATION")].address',
        [this.$t('contact')]:
          'glnRecords[?(@.glnType=="FIXED_PHYSICAL_LOCATION")].contactPoint',
        [this.$t('geo')]:
          'glnRecords[?(@.glnType=="FIXED_PHYSICAL_LOCATION")].geoCoordinates',
        [this.$t('updatedOn')]:
          'glnRecords[?(@.glnType=="FIXED_PHYSICAL_LOCATION")].dateUpdated'
      }

      const glnMLEntries = {
        [this.$t('gln')]: 'gln',
        [this.$t('glnRecordStatus')]:
          'glnRecords[?(@.glnType=="MOBILE_PHYSICAL_LOCATION")].glnRecordStatus',
        [this.$t('PL_name')]:
          'glnRecords[?(@.glnType=="MOBILE_PHYSICAL_LOCATION")].physicalLocationName',
        [this.$t('ML_role')]:
          'glnRecords[?(@.glnType=="MOBILE_PHYSICAL_LOCATION")].locationRole',
        [this.$t('PL_base_location')]:
          'glnRecords[?(@.glnType=="MOBILE_PHYSICAL_LOCATION")].baseLocation.gln',
        [this.$t('contact')]:
          'glnRecords[?(@.glnType=="MOBILE_PHYSICAL_LOCATION")].contactPoint',
        [this.$t('updatedOn')]:
          'glnRecords[?(@.glnType=="MOBILE_PHYSICAL_LOCATION")].dateUpdated'
      }

      if (!this.canExport) return

      if (format == 'xlsx') {
        const workbook = XLSX.utils.book_new()

        if (this.filteredGTINResults.length > 0) {
          const gtinFlat = this.resultToFlatObject(
            gtinEntries,
            this.filteredGTINResults
          )
          const worksheet = XLSX.utils.json_to_sheet(gtinFlat)
          XLSX.utils.book_append_sheet(
            workbook,
            worksheet,
            this.$t('product_info')
          )
          // company
          const gtinCompanyFlat = this.resultToFlatObject(
            gtinLicence,
            this.filteredGTINResults
          )
          console.log(this.filteredGTINResults)
          const Companyworksheet = XLSX.utils.json_to_sheet(gtinCompanyFlat)
          XLSX.utils.book_append_sheet(
            workbook,
            Companyworksheet,
            this.$t('company')
          )
        }

        if (this.filteredGLNResults.length > 0) {
          // digital location
          const glnDLFlat = this.resultToFlatObject(
            glnDLEntries,
            this.filteredGLNResults
          )
          const DLworksheet = XLSX.utils.json_to_sheet(glnDLFlat)
          XLSX.utils.book_append_sheet(
            workbook,
            DLworksheet,
            this.$t('digital_location')
          )
          // Function
          const glnFNFlat = this.resultToFlatObject(
            glnFNEntries,
            this.filteredGLNResults
          )
          const FNworksheet = XLSX.utils.json_to_sheet(glnFNFlat)
          XLSX.utils.book_append_sheet(
            workbook,
            FNworksheet,
            this.$t('function')
          )
          // Legal Entity
          const glnLEFlat = this.resultToFlatObject(
            glnLEEntries,
            this.filteredGLNResults
          )
          const LEworksheet = XLSX.utils.json_to_sheet(glnLEFlat)
          XLSX.utils.book_append_sheet(
            workbook,
            LEworksheet,
            this.$t('legal_entity')
          )
          // Fixed Physical location
          const glnPLFlat = this.resultToFlatObject(
            glnPLEntries,
            this.filteredGLNResults
          )
          const PLworksheet = XLSX.utils.json_to_sheet(glnPLFlat)
          XLSX.utils.book_append_sheet(
            workbook,
            PLworksheet,
            this.$t('fixed_physical_location')
          )
          // Licence
          const glnLIFlat = this.resultToFlatObject(
            glnLicence,
            this.filteredGLNResults
          )
          const LIworksheet = XLSX.utils.json_to_sheet(glnLIFlat)
          XLSX.utils.book_append_sheet(
            workbook,
            LIworksheet,
            this.$t('licence')
          )
          // Mobile Location
          const glnMLFlat = this.resultToFlatObject(
            glnMLEntries,
            this.filteredGLNResults
          )
          const MLworksheet = XLSX.utils.json_to_sheet(glnMLFlat)
          XLSX.utils.book_append_sheet(
            workbook,
            MLworksheet,
            this.$t('mobile_physical_location')
          )
        }

        XLSX.writeFile(workbook, 'verified-results-export.xlsx', {
          compression: true
        })
      } else {
        if (this.filteredGLNResults.length > 0) {
          const glnFlat = this.resultToFlatObject(
            glnEntries,
            this.filteredGLNResults
          )
          var glnBlob = new Blob([this.toCSVString(glnFlat)], {
            type: 'text/csv;charset=utf-8'
          })
          saveAs(glnBlob, 'glns-result-export.' + format)
        }

        if (this.filteredGTINResults.length > 0) {
          const gtinFlat = this.resultToFlatObject(
            gtinEntries,
            this.filteredGTINResults
          )
          var gtinBlob = new Blob([this.toCSVString(gtinFlat)], {
            type: 'text/csv;charset=utf-8'
          })
          saveAs(gtinBlob, 'gtins-result-export.' + format)
        }
      }
    },
    load() {
      this.searchTermErrors = []
      let results = this.gtins.map(gtin => {
        const result = {
          gtin,
          error: null,
          product: null
        }
        try {
          this.validateGtin(gtin)
        } catch (e) {
          result.error = e
          this.searchTermErrors.push('MALFORMED: ' + gtin)
        }
        return result
      })
      const paddedGtins = results
        .filter(result => !result.error)
        .map(result => result.gtin.padStart(14, '0'))
      if (paddedGtins.length === 0) {
        this.results = results
      } else {
        this.loading = true
        this.$verifiedAPI
          .post('/verify_ids', paddedGtins)
          .then(
            response => {
              if (
                !response ||
                !response.data ||
                !Array.isArray(response.data.gtins) ||
                !Array.isArray(response.data.glns)
              ) {
                this.notFound = true
                return
              }

              this.GTINresults = this.saveGet(response, null, 'data', 'gtins')
              this.GLNresults = this.saveGet(response, null, 'data', 'glns')

              if (paddedGtins.length > this.GTINresults.length)
                paddedGtins.map(gtin => {
                  if (this.GTINresults.filter(r => r.gtin == gtin) < 1)
                    this.searchTermErrors.push('NOT_FOUND GTIN: ' + gtin)
                })
              if (paddedGtins.length > this.GLNresults.length)
                paddedGtins.map(gln => {
                  if (
                    this.GLNresults.filter(r => r.gln == gln.substring(1)) < 1
                  )
                    this.searchTermErrors.push(
                      'NOT_FOUND GLN: ' + gln.substring(1)
                    )
                })
            },
            error => {
              const status =
                error && error.response && error.response.status
              if (!status) {
                return
              }
              if (status === 404) {
                // not found
                // try to retrieve company name
                this.notFound = true
              } else if (status === 401) {
                // http client initiates refresh or login redirect
              } else {
                // error
                const e = {
                  key: 'UNKNOWN_ERROR',
                  technicalDetails:
                    (error.response && error.response.data) || {}
                }
                if (status === 400) {
                  if (error.response.data && error.response.data.code === 5) {
                    const validationErrors =
                      error.response.data.validationErrors.filter(
                        v => v.property === 'gtin'
                      )
                    if (
                      typeof validationErrors !== 'undefined' &&
                      validationErrors.length > 0
                    ) {
                      const validationError = validationErrors[0]
                      if (
                        validationError &&
                        validationError.property === 'gtin'
                      ) {
                        const errors = validationError.errors
                        if (errors && errors.includes('E007')) {
                          e.key = 'GTIN_E007'
                        }
                      }
                    }
                  }
                }
                this.setError(e)
              }
            }
          )
          .finally(() => {
            this.loading = false
          })
      }
    },
    lpad(gtin) {
      while (gtin.length < 14) {
        gtin = '0' + gtin
      }
      return gtin
    }
  },
  i18n: {
    messages: {
      de: {
        title: 'Verified by GS1',
        subtitle: 'Mehrfachabfrage Suchergebnisse',
        export: 'Suchergebnisse exportieren',
        only_complete:
          'Nur Produkte mit vollständigen Produktinformationen zeigen',
        search: 'Suchen',
        back: 'Zurück zu den Suchergebnissen',
        contact_gs1: 'kontaktieren Sie GS1',
        description: 'Beschreibung',
        gtin: 'GTIN',
        gln: 'GLN',
        brandName: 'Marke',
        productDescription: 'Produktbeschreibung',
        productImageUrl: 'Produktabbildung URL',
        gpcCategoryCode: 'Kategorie (GPC)',
        netContent: 'Nettofüllmenge',
        countryOfSaleCode: 'Verkaufsländer',
        updatedOn: 'Aktualisiert am',
        licenseeName: 'Lizenznehmer Name',
        licenseeGLN: 'Lizenznehmer GLN',
        licensingMO: 'Lizensierende MO',
        licenceType: 'Lizenztyp',
        licenceKey: 'Lizenzident',
        glnRecordStatus: 'GLN Status',
        no_data: 'Keine Daten verfügbar.',
        registered_to: 'Diese GTIN ist unter der Firma {0} registriert.',
        registered_to_gln: 'Diese Nummer ist unter der Firma {0} registriert.',
        nothing_found:
          'Leider haben wir keine Informationen zu diesem Produkt.',
        product_info: 'Produktinformationen',
        location_info: 'Standortinformationen',
        DL_name: 'Name',
        DL_role: 'Standortrolle',
        DL_digital_address: 'Digitale Adresse',
        contact: 'Kontakt',
        last_update: 'Zuletzt geändert',
        DL_more_locations: 'Weitere Lokation-IDs',
        FN_name: 'Abteilung',
        FN_role: 'Unternehmensrolle',
        parent_company: 'GLN des Mutterunternehmen',
        address: 'Adresse',
        FN_more_organizations: 'Weitere Unternehmens-IDs',
        LE_name: 'Name',
        LE_role: 'Unternehmensrolle',
        PL_name: 'Name',
        PL_more_locations: 'Weitere Lokation-IDs',
        PL_role: 'Lokationsrolle',
        geo: 'Geokoordinaten',
        ML_role: 'Lokationsrolle',
        ML_base_location: 'Basisstandort',
        legal_entity: 'Juristische Person',
        function: 'Funktion',
        fixed_physical_location: 'Feste physische Lokation',
        mobile_physical_location: 'Mobile physische Lokation',
        digital_location: 'Digitale Lokation',
        licence: 'Lizenz',
        company: 'Firma',
        website: 'Webseite',
        errors: {
          INVALID_CHECK_DIGIT:
            'Die Prüfziffer der GTIN ist nicht korrekt, meinten Sie {0}?',
          INVALID_INDICATOR_DIGIT:
            'GS1 Ident ist ungültig oder wird derzeit nicht unterstützt.',
          NON_NUMERIC: 'GS1 GTIN/EAN darf nur Zahlen enthalten.',
          NOT_FOUND:
            'GTIN konnte nicht gefunden werden. Bitte überprüfen Sie Ihre Eingabe, wenn Sie Hilfe benötigen {0}',
          REGISTERED: 'Diese GTIN ist unter der Firma {0} registriert.',
          TOO_SHORT: 'Die eingegebene Nummer ist leider zu kurz für eine GTIN.',
          TOO_LONG: 'Die eingegebene Nummer ist leider zu lang für eine GTIN.'
        }
      },
      en: {
        title: 'Verified by GS1',
        subtitle: 'Bulk search results',
        export: 'Export these results',
        only_complete: 'Show only products with complete data',
        search: 'Search',
        back: 'Back to search results',
        contact_gs1: 'contact GS1',
        description: 'Description',
        gtin: 'GTIN',
        gln: 'GLN',
        brandName: 'Brand',
        productDescription: 'Product description',
        productImageUrl: 'Product image URL',
        gpcCategoryCode: 'Global product category',
        netContent: 'Net contents',
        countryOfSaleCode: 'Countries of sale',
        updatedOn: 'Updated on',
        licenseeName: 'Licensee Name',
        licenseeGLN: 'Licensee GLN',
        licensingMO: 'LicensingMO',
        licenceType: 'Licence Type',
        licenceKey: 'Licence Key',
        glnRecordStatus: 'GLN Status',
        no_data: 'No data available.',
        registered_to: 'This number is registered to {0}',
        registered_to_gln: 'This number is registered to {0}',
        nothing_found:
          'Unfortunately we don’t have any information about the product.',
        product_info: 'Products information',
        location_info: 'Location information',
        DL_name: 'Name',
        DL_role: 'Location role',
        DL_digital_address: 'Digital address',
        contact: 'Contact',
        last_update: 'Last update',
        DL_more_locations: 'Additional location-IDs',
        FN_name: 'Department',
        FN_role: 'Company role',
        parent_company: ' Parentcompany GLN',
        address: 'Address',
        more_organizations: 'Additional organization-IDs',
        LE_name: 'Name',
        LE_role: 'Company role',
        PL_name: 'Name',
        PL_more_locations: 'Additional location-IDs',
        PL_role: 'Location role',
        geo: 'Geocoordinates',
        ML_role: 'Location role',
        ML_base_location: ' Base location',
        legal_entity: 'Legal Entity',
        function: 'Function',
        fixed_physical_location: 'Fixed Physical Location',
        mobile_physical_location: 'Mobile Physical Location',
        digital_location: 'Digital Location',
        licence: 'Licence',
        company: 'Company',
        website: 'Website',
        errors: {
          INVALID_CHECK_DIGIT: 'Incorrect check digit. Did you mean {0}?',
          INVALID_INDICATOR_DIGIT:
            'GS1 key type is invalid or not currently supported.',
          NON_NUMERIC: 'Barcodes can only contain numeric characters',
          NOT_FOUND:
            'GTIN not found. Please check for typos and {0} if you need help',
          REGISTERED: 'This number is registered to {0}.',
          TOO_SHORT: 'This number is too short to be a GS1 barcode number',
          TOO_LONG: 'This number is too long to be a GS1 barcode number'
        }
      }
    }
  }
}
</script>

<style scoped>
.toolkit p {
  color: var(--gs1-dark-gray);
  line-height: 20px;
  font-weight: 300;
  font-size: 17px;
  margin: 0 0 5px 0;
}

.toolkit .btn-group {
  margin: 0 0 50px 0;
}

.toolkit p a {
  text-decoration: none;
}

.toolkit form .checkbox {
  padding-right: 30px;
}

.toolkit .form-search-box input,
.toolkit .form-search-box .btn-search {
  height: 34px;
}

.toolkit .form-search-box input {
  border-color: var(--gs1-light-medium-gray);
  font-size: 16px;
}

.toolkit .form-search-box input::-webkit-input-placeholder,
.toolkit .form-search-box input::-moz-placeholder,
.toolkit .form-search-box input:-ms-input-placeholder,
.toolkit .form-search-box input::placeholder {
  color: var(--gs1-dark-medium-gray);
}

.toolkit .form-search-box .input-wrapper:before {
  margin-top: 9px;
}

.toolkit .form-search-box .btn-search {
  color: var(--gs1-dark-medium-gray);
  padding-top: 7px;
}

.toolkit table.table img {
  max-height: 100%;
  margin: auto;
}

.toolkit table.table td,
.toolkit table.table th {
  font-size: 16px;
  font-weight: normal;
  padding: 0 16px;
  height: 60px;
  vertical-align: middle;
}

.toolkit table.table th {
  border-width: 1px;
}

.toolkit table tr.error td {
  color: var(--gs1-orange);
  background-color: rgba(242, 99, 52, 0.15);
}

.toolkit table tr.error td strong {
  font-weight: bold;
}

.toolkit table.table td.image {
  background: none !important;
  padding: 0;
}

.toolkit table.table td a {
  color: var(--gs1-blue);
  font-weight: bold;
  text-decoration: none;
}

.toolkit table.table td a:hover {
  color: var(--gs1-link);
}

.toolkit table.table tr.error td a {
  color: var(--gs1-link);
  text-decoration: underline;
}

.toolkit table.table tr.error td a:hover {
  color: var(--gs1-blue);
}
</style>



// WEBPACK FOOTER //
// src/router/views/Results.vue